import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";

import PageLayout from "../../layouts/PageLayout";
import { RootState } from "../../store";
import api from "../../api";
import { tryCatch } from "../../utils";
import OnboardingSteps from "./components/OnboardingSteps";
import PlanSelectionForm from "./components/PlanSelectionForm";
import Title from "components/Title";
import Button from "components/Button";

import {
  type ConfigPrice,
  type RenewalOption,
  filterPricesForRenewal,
  getDisplayablePlanPrices,
  getRecurringTwinForPrepaid,
  isPrepaidPrice,
} from "./components/planSelectionPricing";

interface Props {}

const billingStyles = {
  billingFormContainer: {
    width: "100%",
    minHeight: "400px",
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "16px",
  },
  billingMessage: {
    fontSize: "14px",
    fontWeight: 500,
    color: "rgba(26,26,26,0.85)",
  },
  billingAddendum: {
    fontSize: "13px",
    color: "rgba(26,26,26,0.75)",
    lineHeight: 1.5,
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  contentConstraint: {
    width: "100%",
    maxWidth: "92%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  titleIntro: {
    fontSize: "14px",
    color: "rgba(26,26,26,0.85)",
    lineHeight: 1.55,
    marginBottom: "20px",
    maxWidth: "92%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  paymentSectionIntro: {
    width: "100%",
    marginBottom: "20px",
    textAlign: "left" as const,
  },
  paymentSectionTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "rgba(26,26,26,0.95)",
    marginTop: 0,
    marginBottom: "12px",
    lineHeight: 1.3,
  },
  paymentSectionBody: {
    fontSize: "14px",
    color: "rgba(26,26,26,0.85)",
    lineHeight: 1.55,
    marginTop: 0,
    marginBottom: "10px",
  },
  paymentSectionList: {
    margin: "0 0 12px 0",
    paddingLeft: "1.15em",
    listStyleType: "disc",
    fontSize: "14px",
    color: "rgba(26,26,26,0.85)",
    lineHeight: 1.55,
  },
  stickyCta: {
    position: "fixed" as const,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 20,
    background: "#FFFFFF",
    borderTop: "1px solid #E5E7EB",
    padding: "12px 16px",
    display: "flex",
    justifyContent: "center",
    boxShadow: "0 -4px 12px rgba(0,0,0,0.06)",
  },
  stickyCtaInner: {
    width: "100%",
    maxWidth: "420px",
  },
};

const ChoosePlan: React.FC<Props> = () => {
  const { t } = useTranslation("onboarding");
  const history = useHistory();
  const [selectedPrice, setSelectedPrice] = useState<ConfigPrice | null>(null);
  const [renewalOption, setRenewalOption] = useState<RenewalOption>("prepay");

  const [stripeKey, setStripeKey] = useState<string | null>(null);
  const [stripeInstance, setStripeInstance] = useState<Awaited<
    ReturnType<typeof loadStripe>
  > | null>(null);
  const [prices, setPrices] = useState<ConfigPrice[]>([]);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string>("");
  const [checkoutSessionLoading, setCheckoutSessionLoading] = useState(false);
  const [checkoutSessionError, setCheckoutSessionError] = useState<
    string | null
  >(null);
  const [renewLaterPaymentLoading, setRenewLaterPaymentLoading] =
    useState(false);
  const [planChangeCompleted, setPlanChangeCompleted] = useState(false);
  const [planChangeLoading, setPlanChangeLoading] = useState(false);

  const { adapter, subscriptions } = useSelector((state: RootState) => ({
    adapter: state.adapter.adapter,
    subscriptions: state.adapter.subscriptions,
  }));

  const displayablePrices = useMemo(
    () => getDisplayablePlanPrices(prices),
    [prices]
  );

  const isPrepay = !!selectedPrice && isPrepaidPrice(selectedPrice);

  const recurringTwin = useMemo(() => {
    if (!selectedPrice || !isPrepay) return null;
    return getRecurringTwinForPrepaid(selectedPrice, prices);
  }, [selectedPrice, isPrepay, prices]);

  useEffect(() => {
    getConfiguration();
  }, []);

  useEffect(() => {
    if (!stripeKey) return;
    let cancelled = false;
    loadStripe(stripeKey).then((stripe) => {
      if (!cancelled) setStripeInstance(stripe);
    });
    return () => {
      cancelled = true;
    };
  }, [stripeKey]);

  const getConfiguration = async () => {
    const configRes = await tryCatch(api.subscription.getConfig());
    if (configRes.error) {
      console.warn(configRes.error.message);
    } else {
      if (configRes.result.data.publishable_key) {
        setStripeKey(configRes.result.data.publishable_key);
      }
      if (configRes.result.data.prices) {
        setPrices(configRes.result.data.prices);
      }
    }
  };

  const adapterSubscription = adapter
    ? subscriptions?.find((s) => s.adapter_id && s.adapter_id === adapter.id)
    : undefined;

  useEffect(() => {
    if (
      !planChangeCompleted ||
      !isPrepay ||
      !selectedPrice?.id ||
      !adapterSubscription?.subscription_id
    ) {
      if (!planChangeCompleted || !isPrepay) setCheckoutClientSecret("");
      return;
    }
    let cancelled = false;
    setCheckoutSessionError(null);
    setCheckoutSessionLoading(true);
    const run = async () => {
      const res = await tryCatch(
        api.subscription.createOneTimePaymentCheckoutSession(
          selectedPrice.id,
          adapterSubscription.subscription_id,
          undefined,
          "/onboarding/create-primary-network"
        )
      );
      if (cancelled) return;
      setCheckoutSessionLoading(false);
      if (res.error) {
        console.warn(res.error.message);
        setCheckoutSessionError(
          res.error.message ?? "Failed to load payment form"
        );
        setCheckoutClientSecret("");
      } else {
        const secret =
          res.result?.data?.client_secret ??
          (res.result?.data as { data?: { client_secret?: string } })?.data
            ?.client_secret;
        if (secret) setCheckoutClientSecret(secret);
      }
    };
    run();
    return () => {
      cancelled = true;
      setCheckoutSessionLoading(false);
    };
  }, [
    planChangeCompleted,
    isPrepay,
    selectedPrice?.id,
    adapterSubscription?.subscription_id,
  ]);

  const priceIdForChangePlan = isPrepay ? recurringTwin?.id : selectedPrice?.id;

  const canConfirmPlan = !!selectedPrice;

  const handleSelectPrice = (price: ConfigPrice) => {
    setSelectedPrice(price);
    setPlanChangeCompleted(false);
    setCheckoutClientSecret("");
    setCheckoutSessionError(null);
  };

  const handleRenewalOptionChange = useCallback(
    (next: RenewalOption) => {
      setRenewalOption(next);
      setPlanChangeCompleted(false);
      setCheckoutClientSecret("");
      setCheckoutSessionError(null);
      if (selectedPrice) {
        const stillVisible = filterPricesForRenewal(
          displayablePrices,
          next
        ).some((p) => p.id === selectedPrice.id);
        if (!stillVisible) setSelectedPrice(null);
      }
    },
    [displayablePrices, selectedPrice]
  );

  const handleConfirmPlan = async () => {
    if (!selectedPrice) return;
    if (!adapterSubscription?.subscription_id) {
      setCheckoutSessionError(t("choosePlan.connectDeviceToContinue"));
      return;
    }
    if (isPrepay && !recurringTwin?.id) {
      setCheckoutSessionError(t("choosePlan.planTwinUnavailable"));
      return;
    }
    if (!priceIdForChangePlan) {
      setCheckoutSessionError(t("choosePlan.planTwinUnavailable"));
      return;
    }
    setPlanChangeLoading(true);
    setCheckoutSessionError(null);
    const changeRes = await tryCatch(
      api.subscription.changePlan(
        adapterSubscription.subscription_id,
        priceIdForChangePlan
      )
    );
    if (changeRes.error) {
      console.warn(changeRes.error.message);
      setCheckoutSessionError(
        changeRes.error.message ?? "Failed to update plan"
      );
      setPlanChangeLoading(false);
      return;
    }
    setPlanChangeCompleted(true);
    setPlanChangeLoading(false);
  };

  const handleRenewLaterContinueToPayment = async () => {
    if (!adapterSubscription?.subscription_id) return;
    setRenewLaterPaymentLoading(true);
    setCheckoutSessionError(null);
    const sessionRes = await tryCatch(
      api.subscription.createPaymentMethodUpdateCheckoutSession(
        adapterSubscription.subscription_id,
        "/onboarding/create-primary-network"
      )
    );
    if (sessionRes.error) {
      console.warn(sessionRes.error.message);
      setCheckoutSessionError(
        sessionRes.error.message ?? "Failed to load payment form"
      );
    } else {
      const secret =
        sessionRes.result?.data?.client_secret ??
        (sessionRes.result?.data as { data?: { client_secret?: string } })?.data
          ?.client_secret;
      if (secret) setCheckoutClientSecret(secret);
    }
    setRenewLaterPaymentLoading(false);
  };

  const continueButton = (
    <Button
      type="button"
      onClick={handleConfirmPlan}
      disabled={!canConfirmPlan || planChangeLoading}
      className="w-full"
    >
      {planChangeLoading
        ? t("choosePlan.loadingPaymentForm")
        : t("choosePlan.continueToPayment")}
    </Button>
  );

  return (
    <PageLayout>
      <OnboardingSteps stage="choosePlan" />
      <Title title={t("choosePlan.title")} className="mb-4" />
      <div style={billingStyles.titleIntro}>
        <p style={{ marginBottom: 0 }}>{t("choosePlan.pageIntroShort")}</p>
      </div>
      <div
        style={billingStyles.contentConstraint}
        className="choose-plan-content"
      >
        <PlanSelectionForm
          prices={displayablePrices}
          allPricesForSavings={prices}
          selectedPrice={selectedPrice}
          onSelectPrice={handleSelectPrice}
          renewalOption={renewalOption}
          onRenewalOptionChange={handleRenewalOptionChange}
          showRenewalToggle
        />

        {selectedPrice && (
          <>
            {!planChangeCompleted && checkoutSessionError && (
              <div style={billingStyles.billingFormContainer}>
                <p style={{ ...billingStyles.billingMessage, color: "#c00" }}>
                  {checkoutSessionError}
                </p>
              </div>
            )}
            {planChangeCompleted && (
              <>
                <div style={billingStyles.paymentSectionIntro}>
                  <h3 style={billingStyles.paymentSectionTitle}>
                    {t("choosePlan.addPaymentMethodTitle")}
                  </h3>
                  <p style={billingStyles.paymentSectionBody}>
                    {t("choosePlan.addPaymentMethodRequired")}
                  </p>
                  <p
                    style={{
                      ...billingStyles.paymentSectionBody,
                      marginBottom: "6px",
                    }}
                  >
                    {t("choosePlan.addPaymentMethodUsedFor")}
                  </p>
                  <ul style={billingStyles.paymentSectionList}>
                    <li style={{ marginBottom: "4px" }}>
                      {t("choosePlan.addPaymentMethodBulletPrepaid")}
                    </li>
                    <li>{t("choosePlan.addPaymentMethodBulletRenewal")}</li>
                  </ul>
                  <p
                    style={{
                      ...billingStyles.paymentSectionBody,
                      marginBottom: 0,
                    }}
                  >
                    {t("choosePlan.addPaymentMethodNoCharge")}
                  </p>
                </div>
                {isPrepay ? (
                  stripeInstance && checkoutClientSecret ? (
                    <div style={billingStyles.billingFormContainer}>
                      <EmbeddedCheckoutProvider
                        stripe={stripeInstance}
                        options={{
                          clientSecret: checkoutClientSecret,
                          onComplete: () =>
                            history.push("/onboarding/create-primary-network"),
                        }}
                      >
                        <EmbeddedCheckout />
                      </EmbeddedCheckoutProvider>
                    </div>
                  ) : checkoutSessionLoading ? (
                    <div style={billingStyles.billingFormContainer}>
                      <p style={billingStyles.billingMessage}>
                        {t("choosePlan.loadingPaymentForm")}
                      </p>
                    </div>
                  ) : checkoutSessionError ? (
                    <div style={billingStyles.billingFormContainer}>
                      <p
                        style={{
                          ...billingStyles.billingMessage,
                          color: "#c00",
                        }}
                      >
                        {checkoutSessionError}
                      </p>
                    </div>
                  ) : (
                    <div style={billingStyles.billingFormContainer}>
                      <p style={billingStyles.billingMessage}>
                        {t("choosePlan.loadingPaymentForm")}
                      </p>
                    </div>
                  )
                ) : stripeInstance && checkoutClientSecret ? (
                  <div style={billingStyles.billingFormContainer}>
                    <EmbeddedCheckoutProvider
                      stripe={stripeInstance}
                      options={{
                        clientSecret: checkoutClientSecret,
                        onComplete: () =>
                          history.push("/onboarding/create-primary-network"),
                      }}
                    >
                      <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                  </div>
                ) : (
                  <div style={billingStyles.billingFormContainer}>
                    <Button
                      type="button"
                      onClick={handleRenewLaterContinueToPayment}
                      disabled={renewLaterPaymentLoading}
                    >
                      {renewLaterPaymentLoading
                        ? t("choosePlan.loadingPaymentForm")
                        : t("choosePlan.continueToPayment")}
                    </Button>
                    {checkoutSessionError && (
                      <p
                        style={{
                          marginTop: "12px",
                          fontSize: "14px",
                          color: "#c00",
                        }}
                      >
                        {checkoutSessionError}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
            <div style={billingStyles.billingAddendum}>
              <p>{t("choosePlan.billingAddendum1")}</p>
              <p>{t("choosePlan.billingAddendum2")}</p>
            </div>
          </>
        )}

        {!planChangeCompleted && (
          <>
            <style>{`
              .choose-plan-content {
                padding-bottom: 88px;
              }
              .choose-plan-cta-desktop {
                display: none;
                margin-top: 8px;
              }
              .choose-plan-cta-mobile {
                display: flex;
                position: fixed;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 20;
                background: #ffffff;
                border-top: 1px solid #e5e7eb;
                padding: 12px 16px;
                justify-content: center;
                box-shadow: 0 -4px 12px rgba(0,0,0,0.06);
              }
              @media (min-width: 768px) {
                .choose-plan-content {
                  padding-bottom: 0;
                }
                .choose-plan-cta-desktop {
                  display: block;
                }
                .choose-plan-cta-mobile {
                  display: none !important;
                }
              }
            `}</style>
            <div className="choose-plan-cta-desktop">{continueButton}</div>
            <div className="choose-plan-cta-mobile">
              <div style={billingStyles.stickyCtaInner}>{continueButton}</div>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default ChoosePlan;
